import { useEffect, useState } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { RefreshIcon } from 'Asset/icon';
import selfieOverlay from 'Asset/image/selfie-overlay.svg';
import { Condition } from 'Component/others';
import OpenCamera from 'Component/open-camera';
import captureVideoFrame from 'Helper/captureVideoFrame';
import ButtonSolid from 'UiKit/button/buttonSolid';
import { useHostStreamState } from 'View/host-stream/state';
import RetryButton from 'Component/retry-button';

import styles from './styles.module.css';

const StreamRecord = () => {
  const { t } = useTranslation('host-attendance');
  const [isSnapping, setIsSnapping] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const { updateState, state } = useHostStreamState();

  const isFirstCapture = state?.currentStep === 0;
  const defaultFacingMode = isFirstCapture ? 'user' : 'environment';
  const facingMode = isFlipped ? (defaultFacingMode === 'user' ? 'environment' : 'user') : defaultFacingMode;

  const captureImage = () => {
    if (!isCameraReady) return;
    setIsSnapping(true);
    setTimeout(() => setIsSnapping(false), 150);

    const capturedImage = captureVideoFrame({ videoElementId: 'video-stream', mirror: facingMode === 'user' });
    if (!capturedImage) return;

    updateState({ tempCapturedImage: capturedImage });
  };

  const handleNext = () => {
    updateState({
      capturedImage:
        state?.currentStep === 0
          ? { first: state?.tempCapturedImage, second: state?.capturedImage?.second }
          : { first: state?.capturedImage?.first, second: state?.tempCapturedImage },
      currentStep: state?.currentStep === 0 && state?.capturedImage?.second ? 2 : state?.currentStep + 1,
      tempCapturedImage: null,
    });
  };

  useEffect(() => {
    setIsFlipped(false);
  }, [state?.currentStep]);

  return (
    <Box className={styles['stream-start__container']}>
      <Box className={styles['stream-start__top-section']}>
        <Condition statement={state?.tempCapturedImage}>
          <Image src={state?.tempCapturedImage} alt="captured-attendance" className={styles['stream-start__preview-image']} />
        </Condition>
        <Condition statement={!state?.tempCapturedImage}>
          <Condition statement={isFirstCapture}>
            <Image src={selfieOverlay} alt="selfie-overlay" className={styles['stream-start__selfie-overlay']} />
          </Condition>
          <Box className={styles['stream-start__switch-button']} onClick={() => setIsFlipped((prev) => !prev)}>
            <RefreshIcon size="24px" color="var(--color-primary70)" />
          </Box>
          <OpenCamera
            isOpen={true}
            className={`${styles['camera__video']} ${facingMode === 'user' ? styles['camera__video--mirrored'] : ''}`}
            facingMode={facingMode}
            setIsCameraReady={setIsCameraReady}
          />
        </Condition>
      </Box>
      <Box className={styles['stream-start__bottom-section']} onClick={captureImage}>
        <Condition statement={!state?.tempCapturedImage}>
          <Box className={styles['stream-start__instruction-text']}>
            {isFirstCapture ? t('capture_photo.selfie.instruction') : t('capture_photo.work_environment.instruction')}
          </Box>
          <Box
            className={`${styles['stream-start__capture-button']} ${!isCameraReady ? styles['stream-start__capture-button--disabled'] : ''}`}
            onClick={captureImage}
          />
        </Condition>
        <Condition statement={state?.tempCapturedImage}>
          <Box className={styles['stream-start__instruction-text']}>
            <RetryButton size="large" onClick={() => updateState({ tempCapturedImage: null })} />
          </Box>
          <ButtonSolid className={styles['stream-start__next-button']} onClick={handleNext}>
            {t('common:next')}
          </ButtonSolid>
        </Condition>
      </Box>
      <Condition statement={isSnapping}>
        <Box className={styles['stream-start__snap-effect']} />
      </Condition>
    </Box>
  );
};

export default StreamRecord;
